import dayjs from 'dayjs';
import { duration } from './format.ts';

/**
 * How often an integration moves data, from when its runs started: on a schedule (hourly at :05, daily at
 * 18:00, every 15 min) or on events (as things happen, about so many a day). The runs are those of what
 * was found, so for an order-driven integration it is how often it ran for these records.
 */
export interface Rhythm {
  kind: 'hourly' | 'daily' | 'every' | 'events' | 'once';
  /** "daily at 18:00", "on events, about 6 a day" */
  label: string;
  /** "daily 18:00", "≈ 6/day" */
  short: string;
  scheduled: boolean;
  everyMs: number | null;
  perDay: number;
  /** Runs started in each hour of the day (local time), over all days. */
  hours: number[];
  last: number | null;
  /** When the next run is due, for a schedule. */
  next: number | null;
}

const HOUR = 3600_000;
const DAY = 24 * HOUR;
const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  return s.length ? s[Math.floor(s.length / 2)] : 0;
};
const rate = (perDay: number) => {
  if (perDay >= 48) return { long: `about ${Math.round(perDay / 24)} an hour`, short: `≈ ${Math.round(perDay / 24)}/h` };
  if (perDay >= 1) return { long: `about ${Math.round(perDay)} a day`, short: `≈ ${Math.round(perDay)}/day` };
  return { long: `about ${Math.max(1, Math.round(perDay * 7))} a week`, short: `≈ ${Math.max(1, Math.round(perDay * 7))}/week` };
};
/** The share of values within tol of the most common one (on a circle of size mod). */
const together = (xs: number[], mod: number, tol: number) => {
  let best = 0;
  let at = 0;
  for (const c of xs) {
    const n = xs.filter((x) => Math.min(Math.abs(x - c), mod - Math.abs(x - c)) <= tol).length;
    if (n > best) {
      best = n;
      at = c;
    }
  }
  return { share: best / xs.length, at };
};

export function rhythmOf(startsIn: number[]): Rhythm {
  const starts = [...new Set(startsIn)].sort((a, b) => a - b);
  const hours = Array.from({ length: 24 }, () => 0);
  for (const s of starts) hours[dayjs(s).hour()]++;
  const last = starts.at(-1) ?? null;
  const base = { hours, last };
  if (starts.length < 3) {
    return { ...base, kind: 'once', label: starts.length === 1 ? 'ran once' : 'ran twice', short: starts.length === 1 ? 'once' : 'twice', scheduled: false, everyMs: null, perDay: 0, next: null };
  }
  const gaps = starts.slice(1).map((s, i) => s - starts[i]);
  const m = median(gaps);
  const span = starts[starts.length - 1] - starts[0];
  const perDay = span > 0 ? ((starts.length - 1) / span) * DAY : 0;
  const ofDay = starts.map((s) => dayjs(s).hour() * 60 + dayjs(s).minute());
  const ofHour = starts.map((s) => dayjs(s).minute());
  if (m >= 20 * HOUR && m <= 30 * HOUR) {
    const t = together(ofDay, 1440, 30);
    if (t.share >= 0.7) {
      const hm = `${String(Math.floor(t.at / 60)).padStart(2, '0')}:${String(t.at % 60).padStart(2, '0')}`;
      return { ...base, kind: 'daily', label: `daily at ${hm}`, short: `daily ${hm}`, scheduled: true, everyMs: DAY, perDay, next: last! + DAY };
    }
  }
  if (m >= 45 * 60_000 && m <= 80 * 60_000) {
    const t = together(ofHour, 60, 5);
    if (t.share >= 0.7) {
      const mm = `:${String(t.at).padStart(2, '0')}`;
      return { ...base, kind: 'hourly', label: `hourly at ${mm}`, short: `hourly ${mm}`, scheduled: true, everyMs: HOUR, perDay, next: last! + HOUR };
    }
  }
  const mean = gaps.reduce((a, b) => a + b, 0) / gaps.length;
  const sd = Math.sqrt(gaps.reduce((a, b) => a + (b - mean) ** 2, 0) / gaps.length);
  if (mean > 0 && sd / mean < 0.3) {
    return { ...base, kind: 'every', label: `every ${duration(m)}`, short: `every ${duration(m)}`, scheduled: true, everyMs: m, perDay, next: last! + m };
  }
  const r = rate(perDay);
  return { ...base, kind: 'events', label: `on events, ${r.long}`, short: r.short, scheduled: false, everyMs: null, perDay, next: null };
}

/** A schedule's run is late when the next one is overdue by more than a quarter of its interval (5 min at least). */
export const late = (r: Rhythm, now: number) => !!(r.scheduled && r.next && r.everyMs && now > r.next + Math.max(5 * 60_000, r.everyMs / 4));
